// Anonymous interaction tracker for unauthenticated visitors.
//
// Records a `view` when an anonymous visitor opens an event (clicks a card →
// navigates to detail, or arrives via a direct/shared link). There is no
// passive view tracking — a view means an actual event-open. Posts to
// /anonymous/interactions. Hard-gated on the absence of a valid JWT so we
// never double-count: authed users go through createInteraction(...,'view')
// (POST /interactions) instead, and that path stays the source of truth for
// state (likes/dislikes/etc).
//
// Batching: in-memory queue flushed on any of:
//   - size ≥ BATCH_FLUSH_SIZE
//   - DEBOUNCE_MS elapsed since last enqueue
//   - pagehide / beforeunload (best-effort via fetch keepalive)
//
// Best-effort telemetry: anything still queued when the tab is closed before
// the keepalive fetches resolve may be lost. Acceptable for anonymous v1.
// sendBeacon is intentionally NOT used — it can't set X-Device-Id headers
// and the server identifies callers strictly from headers, so every beacon
// would 422.

import { getApplaussAccessToken, getAnonymousHeaders, hasAnonymousConsent } from './authManager';
import { getBackendBaseUrl } from './backendUrl';
import i18n from '../i18n/config';

const BATCH_FLUSH_SIZE = 10;
const DEBOUNCE_MS = 5000;

/** @type {Array<{event_id: string, interaction_type: string}>} */
const queue = [];
let flushTimer = null;
let unloadHandlerInstalled = false;

function shouldTrack() {
  // Authed users use POST /interactions instead — short-circuit hard here.
  if (getApplaussAccessToken() !== null) return false;
  // Default-deny: an undecided visitor is tracked no more than one who declined.
  if (!hasAnonymousConsent()) return false;
  return true;
}

function scheduleFlush() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, DEBOUNCE_MS);
}

function installUnloadHandlerOnce() {
  if (unloadHandlerInstalled || typeof window === 'undefined') return;
  unloadHandlerInstalled = true;
  // pagehide is the modern unload event (better bfcache behavior than
  // beforeunload). Fall back to beforeunload for older browsers.
  const handler = () => {
    if (queue.length === 0) return;
    void flushQueue();
  };
  window.addEventListener('pagehide', handler);
  window.addEventListener('beforeunload', handler);
}

async function flushQueue() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const headers = {
    'Content-Type': 'application/json',
    // Not through the axios instance, so the UI language is attached here too —
    // the API records it as the device's telemetry `locale`.
    'Accept-Language': i18n.language,
    ...getAnonymousHeaders(),
  };
  if (!headers['X-Device-Id']) {
    // No consent (or no device_id yet) — drop the batch silently.
    return;
  }
  const baseUrl = getBackendBaseUrl();
  // Fire all in parallel with keepalive so the unload path can issue the
  // requests before the document is torn down. Anonymous interactions are
  // independent rows; we don't retry on failure (best-effort telemetry).
  await Promise.allSettled(
    batch.map((item) =>
      fetch(`${baseUrl}/anonymous/interactions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(item),
        keepalive: true,
      }).catch((err) => {
        console.debug('anonymous interaction flush failed', err);
      })
    )
  );
}

function enqueue(eventId, interactionType, extra) {
  if (!shouldTrack()) return;
  if (!eventId) return;
  installUnloadHandlerOnce();
  queue.push({ event_id: eventId, interaction_type: interactionType, ...extra });
  if (queue.length >= BATCH_FLUSH_SIZE) {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    void flushQueue();
  } else {
    scheduleFlush();
  }
}

/**
 * Record a view when an anonymous visitor opens an event (card click →
 * detail navigation, or direct/shared-link arrival). No-op for authenticated
 * users — they record the view via POST /interactions instead.
 * @param {string} eventId
 */
export function trackView(eventId) {
  enqueue(eventId, 'view');
}

/**
 * Record a like for an anonymous visitor. Write-only until merge — local UI
 * toggles in-session but state isn't re-fetched on refresh. No-op for authed
 * users (they go through POST /interactions).
 * @param {string} eventId
 */
export function trackLike(eventId) {
  enqueue(eventId, 'like');
}

/** Record a dislike. @param {string} eventId */
export function trackDislike(eventId) {
  enqueue(eventId, 'dislike');
}

/** Cancel a previous like (toggle off). @param {string} eventId */
export function trackRemoveLike(eventId) {
  enqueue(eventId, 'remove_like');
}

/** Cancel a previous dislike (toggle off). @param {string} eventId */
export function trackRemoveDislike(eventId) {
  enqueue(eventId, 'remove_dislike');
}

/** Record a share. @param {string} eventId */
export function trackShare(eventId) {
  enqueue(eventId, 'share');
}

/** Record an add-to-calendar. @param {string} eventId */
export function trackAddToCalendar(eventId) {
  enqueue(eventId, 'add_to_calendar');
}

/** Record a buy-ticket click. @param {string} eventId */
export function trackBuyTicket(eventId) {
  enqueue(eventId, 'buy_ticket');
}

/** Record an audio-preview play. @param {string} eventId */
export function trackPlayPreview(eventId) {
  enqueue(eventId, 'play_preview');
}

/**
 * Record a referral-link open by an anonymous visitor. The sharer's user id
 * (from ?ref=) rides along as referrer_id; it migrates on merge (unless it
 * would self-refer or the referrer is gone). Anonymous visitors can never be
 * the referrer themselves — they have no user id to embed.
 * @param {string} eventId
 * @param {string} referrerId
 */
export function trackReferralOpen(eventId, referrerId) {
  if (!referrerId) return;
  enqueue(eventId, 'referral_open', { referrer_id: referrerId });
}

/**
 * Flush the queue immediately. Exposed for tests and manual triggers.
 */
export const _internal = {
  flushQueue,
  resetForTests: () => {
    queue.length = 0;
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  },
};
